// Premium Synthesized Web Audio API Sound Effects
let audioCtx = null;
let soundFxEnabled = true; // Default, will be synced from Game.jsx on init

export function isSoundEnabled() {
  return soundFxEnabled;
}

export function setSoundEnabled(enabled) {
  soundFxEnabled = enabled;
}

function getAudioContext() {
  if (!soundFxEnabled) {
    const dummyParam = {
      setValueAtTime: () => dummyParam,
      exponentialRampToValueAtTime: () => dummyParam,
      linearRampToValueAtTime: () => dummyParam
    };
    return {
      currentTime: Date.now() / 1000,
      destination: {},
      createOscillator: () => ({
        type: 'sine',
        frequency: dummyParam,
        connect: () => {},
        start: () => {},
        stop: () => {}
      }),
      createGain: () => ({
        gain: dummyParam,
        connect: () => {}
      }),
      createBiquadFilter: () => ({
        type: 'lowpass',
        frequency: dummyParam,
        Q: dummyParam,
        connect: () => {}
      })
    };
  }
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
}

// 1. Tiny step sound when token jumps squares
export function playStepSound() {
  try {
    const ctx = getAudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(700, ctx.currentTime);
    
    gain.gain.setValueAtTime(0.05, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.start();
    osc.stop(ctx.currentTime + 0.06);
  } catch (e) {
    console.error('Audio play failed', e);
  }
}

// 2. Dice roll rattle sound
export function playDiceSound() {
  try {
    const ctx = getAudioContext();
    
    // Play multiple rolling tick-clacks
    for (let i = 0; i < 5; i++) {
      const delay = i * 0.12;
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(150 + Math.random() * 200, ctx.currentTime);
        
        gain.gain.setValueAtTime(0.1, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
        
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        osc.start();
        osc.stop(ctx.currentTime + 0.09);
      }, delay * 1000);
    }
  } catch (e) {
    console.error(e);
  }
}

// 3. Cash register ka-ching!
export function playCashSound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    
    // Low frequency coin sound followed by high pitch chime
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.frequency.setValueAtTime(587.33, now); // D5
    gain1.gain.setValueAtTime(0.07, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.frequency.setValueAtTime(880, now + 0.08); // A5
    gain2.gain.setValueAtTime(0.07, now + 0.08);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    
    osc1.start();
    osc2.start(now + 0.08);
    
    osc1.stop(now + 0.26);
    osc2.stop(now + 0.45);
  } catch (e) {
    console.error(e);
  }
}

// 4. Success / Buy sound
export function playSuccessSound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    
    const freqs = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
    freqs.forEach((f, index) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(f, now + index * 0.08);
      
      gain.gain.setValueAtTime(0.08, now + index * 0.08);
      gain.gain.exponentialRampToValueAtTime(0.001, now + index * 0.08 + 0.2);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.start(now + index * 0.08);
      osc.stop(now + index * 0.08 + 0.22);
    });
  } catch (e) {
    console.error(e);
  }
}

// 5. Jail door slam (low thud)
export function playJailSound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(90, now);
    osc.frequency.exponentialRampToValueAtTime(30, now + 0.3);
    
    gain.gain.setValueAtTime(0.15, now);
    gain.gain.linearRampToValueAtTime(0.001, now + 0.35);
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.start();
    osc.stop(now + 0.38);
  } catch (e) {
    console.error(e);
  }
}

// 6. Honk horn sound! (Retro Volkswagen Dual-Tone "honkkk" air horn)
export function playHonkSound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    
    const playSingleTone = (freq, startTime, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(freq, startTime);
      
      // Pitch vibration rattle modulation
      const modOsc = ctx.createOscillator();
      const modGain = ctx.createGain();
      modOsc.frequency.value = 45;
      modGain.gain.value = 8;
      modOsc.connect(modGain);
      modGain.connect(osc.frequency);
      
      filter.type = 'bandpass';
      filter.frequency.setValueAtTime(1000, startTime);
      filter.Q.value = 1.8;
      
      gain.gain.setValueAtTime(0.0, startTime);
      gain.gain.linearRampToValueAtTime(0.08, startTime + 0.02);
      gain.gain.setValueAtTime(0.08, startTime + duration - 0.06);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
      
      osc.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);
      
      modOsc.start(startTime);
      osc.start(startTime);
      
      modOsc.stop(startTime + duration);
      osc.stop(startTime + duration);
    };

    // Play retro harmonizing dual tones (370Hz and 410Hz)
    playSingleTone(370, now, 0.38);
    playSingleTone(410, now, 0.38);
  } catch (e) {
    console.error('Honk play failed', e);
  }
}

// 7. Message bubble pop sound
export function playChatSound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(350, now);
    osc.frequency.exponentialRampToValueAtTime(700, now + 0.08);
    
    gain.gain.setValueAtTime(0.06, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.start();
    osc.stop(now + 0.12);
  } catch (e) {
    console.error('Chat sound play failed', e);
  }
}

// 8. pass GO cash register + gold coin cascade payout sound
export function playVapurDudugu() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gainNode = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc1.type = 'sawtooth';
    osc1.frequency.setValueAtTime(110, now); // Low A

    osc2.type = 'sawtooth';
    osc2.frequency.setValueAtTime(111.5, now); // Slightly detuned

    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(350, now);

    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.12, now + 0.15); // Fade in
    gainNode.gain.setValueAtTime(0.12, now + 1.2);
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + 1.6); // Fade out

    osc1.connect(filter);
    osc2.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(ctx.destination);

    osc1.start(now);
    osc2.start(now);
    osc1.stop(now + 1.65);
    osc2.stop(now + 1.65);
  } catch (e) {
    console.error('Vapur düdüğü play failed', e);
  }
}

// 9. railroad/station ding-dong bell
export function playIstasyonZili() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(880, now);
    gain1.gain.setValueAtTime(0.06, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start();
    osc1.stop(now + 0.45);
    
    setTimeout(() => {
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(659.25, ctx.currentTime);
      gain2.gain.setValueAtTime(0.06, ctx.currentTime);
      gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start();
      osc2.stop(ctx.currentTime + 0.45);
    }, 250);
  } catch (e) {}
}

// 10. tax pay drain sound
export function playTaxPaySound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(440, now);
    osc.frequency.exponentialRampToValueAtTime(150, now + 0.35);
    gain.gain.setValueAtTime(0.06, now);
    gain.gain.linearRampToValueAtTime(0.001, now + 0.38);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(now + 0.4);
  } catch (e) {}
}

// 11. card draw comedy slide sound
export function playCardDrawSound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(250, now);
    osc.frequency.exponentialRampToValueAtTime(800, now + 0.25);
    gain.gain.setValueAtTime(0.05, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.28);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(now + 0.3);
  } catch (e) {}
}

// 12. hotel built celebration arpeggio
export function playHotelBuiltSound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.50];
    notes.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(f, now + i * 0.05);
      gain.gain.setValueAtTime(0.06, now + i * 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.05 + 0.15);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.05);
      osc.stop(now + i * 0.05 + 0.18);
    });
  } catch (e) {}
}

// 13. otopark pool victory sound
export function playBigWinSound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const notes = [523.25, 587.33, 659.25, 698.46, 783.99, 880.00, 987.77, 1046.50];
    notes.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(f, now + i * 0.04);
      gain.gain.setValueAtTime(0.05, now + i * 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.04 + 0.12);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.04);
      osc.stop(now + i * 0.04 + 0.15);
    });
  } catch (e) {}
}

// 14. Turn warning tick sound (last 10 seconds)
export function playTickSound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'triangle';
    osc.frequency.setValueAtTime(880, now);
    gain.gain.setValueAtTime(0.08, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.1);
  } catch (e) {}
}

// 15. Double salary gold coins dropping sound
export function playDoubleSalarySound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    for (let i = 0; i < 6; i++) {
      const delay = i * 0.08;
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(1200 + Math.random() * 600, ctx.currentTime);
        gain.gain.setValueAtTime(0.06, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.13);
      }, delay * 1000);
    }
    setTimeout(() => {
      playCashSound();
    }, 450);
  } catch (e) {
    console.error(e);
  }
}

// 16. Turn alert sound (triple pleasant chimes)
export function playTurnAlertSound() {
  try {
    const ctx = getAudioContext();
    for (let i = 0; i < 3; i++) {
      const delay = i * 0.12;
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(700 + i * 150, ctx.currentTime);
        gain.gain.setValueAtTime(0.06, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.16);
      }, delay * 1000);
    }
  } catch (e) {
    console.error(e);
  }
}

// 17. Metro warning whistle
export function playMetroSound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    const gain2 = ctx.createGain();

    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(659.25, now); // E5
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.05, now + 0.05);
    gain1.gain.setValueAtTime(0.05, now + 0.3);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.5);

    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(554.37, now + 0.1); // C#5
    gain2.gain.setValueAtTime(0, now + 0.1);
    gain2.gain.linearRampToValueAtTime(0.05, now + 0.15);
    gain2.gain.setValueAtTime(0.05, now + 0.4);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.6);

    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.55);

    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.1);
    osc2.stop(now + 0.65);
  } catch (e) {
    console.error('Metro sound play failed', e);
  }
}

// 18. Gediz Electric buzz sound
export function playElectricSound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gainNode = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(50, now); // 50 Hz hum

    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(150, now);
    filter.Q.setValueAtTime(2.0, now);

    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.08, now + 0.05);

    for (let i = 0; i < 4; i++) {
      const snapTime = now + 0.1 + i * 0.15 + Math.random() * 0.05;
      const oscSnap = ctx.createOscillator();
      const gainSnap = ctx.createGain();
      oscSnap.type = 'triangle';
      oscSnap.frequency.setValueAtTime(1000 + Math.random() * 2000, snapTime);
      gainSnap.gain.setValueAtTime(0.04, snapTime);
      gainSnap.gain.exponentialRampToValueAtTime(0.001, snapTime + 0.04);
      oscSnap.connect(gainSnap);
      gainSnap.connect(ctx.destination);
      oscSnap.start(snapTime);
      oscSnap.stop(snapTime + 0.05);
    }

    gainNode.gain.setValueAtTime(0.08, now + 0.6);
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.8);

    osc.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.85);
  } catch (e) {
    console.error('Electric sound play failed', e);
  }
}

// 19. IZSU bubbling water sound
export function playWaterSound() {
  try {
    const ctx = getAudioContext();
    for (let i = 0; i < 6; i++) {
      const delay = i * 0.08 + Math.random() * 0.03;
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        const startFreq = 400 + Math.random() * 300;
        osc.frequency.setValueAtTime(startFreq, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(startFreq * 2.2, ctx.currentTime + 0.08);

        gain.gain.setValueAtTime(0.06, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);

        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.09);
      }, delay * 1000);
    }
  } catch (e) {
    console.error('Water sound play failed', e);
  }
}

// 20. Cigdem seed cracking sound
export function playCigdemSound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'triangle';
    osc1.frequency.setValueAtTime(2500, now);
    osc1.frequency.exponentialRampToValueAtTime(100, now + 0.03);
    gain1.gain.setValueAtTime(0.07, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.03);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.04);

    setTimeout(() => {
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'triangle';
      osc2.frequency.setValueAtTime(1800, ctx.currentTime);
      osc2.frequency.exponentialRampToValueAtTime(150, ctx.currentTime + 0.02);
      gain2.gain.setValueAtTime(0.05, ctx.currentTime);
      gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.02);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start();
      osc2.stop(ctx.currentTime + 0.03);
    }, 45);
  } catch (e) {
    console.error('Cigdem sound play failed', e);
  }
}
